import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack
} from '@mui/material';
import {
  addDays,
  addMonths,
  addYears,
  format,
  parse,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subDays,
  subMonths,
  subYears
} from 'date-fns';
import DatePickerComponent from '../ui/datePickerComponent';
import { SelectLocation } from '../common';
import CustomerSearch from '../customerSearch/customerSearch';
import useLocation from '../../hooks/useLocation';

const ReportFilters = ({ selectedReport, filters, setFilters }) => {
  const { selectedLocation } = useLocation();

  useEffect(() => {
    if ((selectedReport === 'sales' || selectedReport === 'dispense' || selectedReport === 'customers' || selectedReport === 'pending-insurance-claims' || selectedReport === 'payment-methods' || selectedReport === 'payments-received' || selectedReport === 'insurance-received' || selectedReport === 'due-eye-exam' || selectedReport === 'rx-due-soon' || selectedReport === 'rx-expired') && !filters.locationId && selectedLocation?.id) {
      setFilters((prev) => ({ ...prev, locationId: selectedLocation.id }));
    }
  }, [selectedReport, selectedLocation?.id, filters.locationId, setFilters]);

  if (!selectedReport) return null;

  const applyDatePreset = (preset) => {
    const now = new Date();
    let dateFrom = filters.dateFrom;
    let dateTo = filters.dateTo;

    if (preset === 'daily') {
      const today = format(now, 'yyyy-MM-dd');
      dateFrom = today;
      dateTo = today;
    } else if (preset === 'monthly') {
      dateFrom = format(startOfMonth(now), 'yyyy-MM-dd');
      dateTo = format(endOfMonth(now), 'yyyy-MM-dd');
    } else if (preset === 'yearly') {
      dateFrom = format(startOfYear(now), 'yyyy-MM-dd');
      dateTo = format(endOfYear(now), 'yyyy-MM-dd');
    }

    setFilters((prev) => ({ ...prev, datePreset: preset, dateFrom, dateTo, page: 1 }));
  };

  // Parse YYYY-MM-DD as a local date to avoid UTC shift issues from new Date('YYYY-MM-DD').
  const parseFilterDate = (value, fallback = new Date()) => {
    if (!value || typeof value !== 'string') return fallback;
    const parsed = parse(value, 'yyyy-MM-dd', fallback);
    return Number.isNaN(parsed.getTime()) ? fallback : parsed;
  };

  const shiftDateRangeByPreset = (direction = 'next') => {
    const preset = filters.datePreset || 'daily';
    const fromDate = parseFilterDate(filters.dateFrom, new Date());
    const toDate = parseFilterDate(filters.dateTo, fromDate);
    const shiftBy = direction === 'next' ? 1 : -1;

    let nextFrom = fromDate;
    let nextTo = toDate;

    if (preset === 'monthly') {
      nextFrom = shiftBy > 0 ? addMonths(fromDate, 1) : subMonths(fromDate, 1);
      nextTo = shiftBy > 0 ? addMonths(toDate, 1) : subMonths(toDate, 1);
    } else if (preset === 'yearly') {
      nextFrom = shiftBy > 0 ? addYears(fromDate, 1) : subYears(fromDate, 1);
      nextTo = shiftBy > 0 ? addYears(toDate, 1) : subYears(toDate, 1);
    } else {
      nextFrom = shiftBy > 0 ? addDays(fromDate, 1) : subDays(fromDate, 1);
      nextTo = shiftBy > 0 ? addDays(toDate, 1) : subDays(toDate, 1);
    }

    setFilters((prev) => ({
      ...prev,
      dateFrom: format(nextFrom, 'yyyy-MM-dd'),
      dateTo: format(nextTo, 'yyyy-MM-dd'),
      page: 1
    }));
  };

  const shiftSingleDate = (field, unit = 'day', direction = 'next') => {
    const baseDate = parseFilterDate(filters[field], new Date());
    const forward = direction === 'next';

    let nextDate = baseDate;
    if (unit === 'month') {
      nextDate = forward ? addMonths(baseDate, 1) : subMonths(baseDate, 1);
    } else if (unit === 'year') {
      nextDate = forward ? addYears(baseDate, 1) : subYears(baseDate, 1);
    } else if (unit === 'week') {
      nextDate = forward ? addDays(baseDate, 7) : subDays(baseDate, 7);
    } else {
      nextDate = forward ? addDays(baseDate, 1) : subDays(baseDate, 1);
    }

    setFilters((prev) => ({
      ...prev,
      [field]: format(nextDate, 'yyyy-MM-dd'),
      page: 1
    }));
  };

  const shiftMonthYear = (direction = 'next') => {
    const year = Number(filters.year) || new Date().getFullYear();
    const month = Number(filters.month) || 1;
    const base = new Date(year, month - 1, 1);
    const next = direction === 'next' ? addMonths(base, 1) : subMonths(base, 1);

    setFilters((prev) => ({
      ...prev,
      year: next.getFullYear(),
      month: next.getMonth() + 1,
      page: 1
    }));
  };

  const PeriodNavigator = ({ onPrev, onNext }) => (
    <Stack direction="row" spacing={1}>
      <Button size="small" variant="outlined" onClick={onPrev}>Prev</Button>
      <Button size="small" variant="outlined" onClick={onNext}>Next</Button>
    </Stack>
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>Report Filters</Typography>
      <Grid container spacing={2}>
        {(selectedReport === 'sales') && (
          <>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={filters.datePreset === 'daily' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('daily')}
                >
                  Daily
                </Button>
                <Button
                  variant={filters.datePreset === 'monthly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('monthly')}
                >
                  Monthly
                </Button>
                <Button
                  variant={filters.datePreset === 'yearly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('yearly')}
                >
                  Yearly
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <PeriodNavigator
                onPrev={() => shiftDateRangeByPreset('prev')}
                onNext={() => shiftDateRangeByPreset('next')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateFrom"
                id="sales-date-from"
                value={filters.dateFrom}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateFrom: date || prev.dateFrom, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateTo"
                id="sales-date-to"
                value={filters.dateTo}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateTo: date || prev.dateTo, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={filters.invoiceType || 'sales'}
                  label="Report Type"
                  onChange={(e) => setFilters(prev => ({ ...prev, invoiceType: e.target.value, page: 1 }))}
                >
                  <MenuItem value="sales">Sales</MenuItem>
                  <MenuItem value="return">Returns</MenuItem>
                  <MenuItem value="warranty">Warranty</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <SelectLocation
                location={filters.locationId}
                setLocation={(location) => setFilters(prev => ({
                  ...prev,
                  locationId: location ? location.id : null,
                  page: 1
                }))}
                fullWidth
                required
                helperText="Required"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size='small'>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={filters.limit}
                  label="Items per page"
                  onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}        
        {(selectedReport === 'dispense') && (
          <>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={filters.datePreset === 'daily' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('daily')}
                >
                  Daily
                </Button>
                <Button
                  variant={filters.datePreset === 'monthly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('monthly')}
                >
                  Monthly
                </Button>
                <Button
                  variant={filters.datePreset === 'yearly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('yearly')}
                >
                  Yearly
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <PeriodNavigator
                onPrev={() => shiftDateRangeByPreset('prev')}
                onNext={() => shiftDateRangeByPreset('next')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateFrom"
                id="sales-date-from"
                value={filters.dateFrom}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateFrom: date || prev.dateFrom, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateTo"
                id="sales-date-to"
                value={filters.dateTo}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateTo: date || prev.dateTo, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <SelectLocation
                location={filters.locationId}
                setLocation={(location) => setFilters(prev => ({
                  ...prev,
                  locationId: location ? location.id : null,
                  page: 1
                }))}
                fullWidth
                required
                helperText="Required"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size='small'>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={filters.limit}
                  label="Items per page"
                  onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}
        {(selectedReport === 'pending-insurance-claims') && (
          <>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={filters.datePreset === 'daily' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('daily')}
                >
                  Daily
                </Button>
                <Button
                  variant={filters.datePreset === 'monthly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('monthly')}
                >
                  Monthly
                </Button>
                <Button
                  variant={filters.datePreset === 'yearly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('yearly')}
                >
                  Yearly
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <PeriodNavigator
                onPrev={() => shiftDateRangeByPreset('prev')}
                onNext={() => shiftDateRangeByPreset('next')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateFrom"
                id="sales-date-from"
                value={filters.dateFrom}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateFrom: date || prev.dateFrom, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateTo"
                id="sales-date-to"
                value={filters.dateTo}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateTo: date || prev.dateTo, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <SelectLocation
                location={filters.locationId}
                setLocation={(location) => setFilters(prev => ({
                  ...prev,
                  locationId: location ? location.id : null,
                  page: 1
                }))}
                fullWidth
                required
                helperText="Required"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size='small'>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={filters.limit}
                  label="Items per page"
                  onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}
        {selectedReport === 'daily-sales' && (
          <>
            <Grid item xs={12}>
              <PeriodNavigator
                onPrev={() => shiftSingleDate('date', 'day', 'prev')}
                onNext={() => shiftSingleDate('date', 'day', 'next')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DatePickerComponent
                name="date"
                id="report-date"
                value={filters.date}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, date: date || prev.date }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SelectLocation
                location={filters.locationId}
                setLocation={(location) => setFilters(prev => ({ 
                  ...prev, 
                  locationId: location ? location.id : '' 
                }))}
                fullWidth
                required
                helperText="Required - Select location for report"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size='small'>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={filters.limit}
                  onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}
        
        {selectedReport === 'monthly-sales' && (
          <>
            <Grid item xs={12}>
              <PeriodNavigator
                onPrev={() => shiftMonthYear('prev')}
                onNext={() => shiftMonthYear('next')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Year"
                type="number"
                value={filters.year}
                onChange={(e) => setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Month"
                type="number"
                inputProps={{ min: 1, max: 12 }}
                value={filters.month}
                onChange={(e) => setFilters(prev => ({ ...prev, month: parseInt(e.target.value) }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SelectLocation
                location={filters.locationId}
                setLocation={(location) => setFilters(prev => ({ 
                  ...prev, 
                  locationId: location ? location.id : '' 
                }))}
                fullWidth
                helperText="Optional - Select location to filter by"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size='small'>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={filters.limit}
                  onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {selectedReport === 'inventory' && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Product Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="frame">Frames</MenuItem>
                <MenuItem value="lens">Lenses</MenuItem>
                <MenuItem value="contacts">Contacts</MenuItem>
                <MenuItem value="accessories">Accessories</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        {selectedReport === 'customers' && (
          <>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={filters.datePreset === 'daily' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('daily')}
                >
                  Daily
                </Button>
                <Button
                  variant={filters.datePreset === 'monthly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('monthly')}
                >
                  Monthly
                </Button>
                <Button
                  variant={filters.datePreset === 'yearly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('yearly')}
                >
                  Yearly
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <PeriodNavigator
                onPrev={() => shiftDateRangeByPreset('prev')}
                onNext={() => shiftDateRangeByPreset('next')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateFrom"
                id="customer-report-date-from"
                value={filters.dateFrom}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateFrom: date || prev.dateFrom, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateTo"
                id="customer-report-date-to"
                value={filters.dateTo}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateTo: date || prev.dateTo, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SelectLocation
                location={filters.locationId}
                setLocation={(location) => setFilters(prev => ({
                  ...prev,
                  locationId: location ? location.id : null,
                  page: 1
                }))}
                fullWidth
                required
                helperText="Required"
              />
            </Grid>
          </>
        )}

        {selectedReport === 'payment-methods' && (
          <>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={filters.datePreset === 'daily' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('daily')}
                >
                  Daily
                </Button>
                <Button
                  variant={filters.datePreset === 'monthly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('monthly')}
                >
                  Monthly
                </Button>
                <Button
                  variant={filters.datePreset === 'yearly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('yearly')}
                >
                  Yearly
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <PeriodNavigator
                onPrev={() => shiftDateRangeByPreset('prev')}
                onNext={() => shiftDateRangeByPreset('next')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateFrom"
                id="payment-methods-date-from"
                value={filters.dateFrom}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateFrom: date || prev.dateFrom, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateTo"
                id="payment-methods-date-to"
                value={filters.dateTo}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateTo: date || prev.dateTo, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SelectLocation
                location={filters.locationId}
                setLocation={(location) => setFilters(prev => ({
                  ...prev,
                  locationId: location ? location.id : null,
                  page: 1
                }))}
                fullWidth
                required
                helperText="Required"
              />
            </Grid>
          </>
        )}

        {selectedReport === 'payments-received' && (
          <>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={filters.datePreset === 'daily' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('daily')}
                >
                  Daily
                </Button>
                <Button
                  variant={filters.datePreset === 'monthly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('monthly')}
                >
                  Monthly
                </Button>
                <Button
                  variant={filters.datePreset === 'yearly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('yearly')}
                >
                  Yearly
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <PeriodNavigator
                onPrev={() => shiftDateRangeByPreset('prev')}
                onNext={() => shiftDateRangeByPreset('next')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateFrom"
                id="payments-received-date-from"
                value={filters.dateFrom}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateFrom: date || prev.dateFrom, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateTo"
                id="payments-received-date-to"
                value={filters.dateTo}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateTo: date || prev.dateTo, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SelectLocation
                location={filters.locationId}
                setLocation={(location) => setFilters(prev => ({
                  ...prev,
                  locationId: location ? location.id : null,
                  page: 1
                }))}
                fullWidth
                required
                helperText="Required"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size='small'>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={filters.limit}
                  label="Items per page"
                  onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {selectedReport === 'insurance-received' && (
          <>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={filters.datePreset === 'daily' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('daily')}
                >
                  Daily
                </Button>
                <Button
                  variant={filters.datePreset === 'monthly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('monthly')}
                >
                  Monthly
                </Button>
                <Button
                  variant={filters.datePreset === 'yearly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('yearly')}
                >
                  Yearly
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <PeriodNavigator
                onPrev={() => shiftDateRangeByPreset('prev')}
                onNext={() => shiftDateRangeByPreset('next')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateFrom"
                id="insurance-received-date-from"
                value={filters.dateFrom}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateFrom: date || prev.dateFrom, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateTo"
                id="insurance-received-date-to"
                value={filters.dateTo}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateTo: date || prev.dateTo, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SelectLocation
                location={filters.locationId}
                setLocation={(location) => setFilters(prev => ({
                  ...prev,
                  locationId: location ? location.id : null,
                  page: 1
                }))}
                fullWidth
                required
                helperText="Required"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size='small'>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={filters.limit}
                  label="Items per page"
                  onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {selectedReport === 'due-eye-exam' && (
          <>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={filters.datePreset === 'daily' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('daily')}
                >
                  Daily
                </Button>
                <Button
                  variant={filters.datePreset === 'monthly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('monthly')}
                >
                  Monthly
                </Button>
                <Button
                  variant={filters.datePreset === 'yearly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('yearly')}
                >
                  Yearly
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <PeriodNavigator
                onPrev={() => shiftDateRangeByPreset('prev')}
                onNext={() => shiftDateRangeByPreset('next')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateFrom"
                id="due-eye-exam-date-from"
                value={filters.dateFrom}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateFrom: date || prev.dateFrom, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateTo"
                id="due-eye-exam-date-to"
                value={filters.dateTo}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateTo: date || prev.dateTo, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SelectLocation
                location={filters.locationId}
                setLocation={(location) => setFilters(prev => ({
                  ...prev,
                  locationId: location ? location.id : null,
                  page: 1
                }))}
                fullWidth
                required
                helperText="Required"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size='small'>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={filters.limit}
                  label="Items per page"
                  onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {selectedReport === 'rx-due-soon' && (
          <>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={filters.datePreset === 'daily' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('daily')}
                >
                  Daily
                </Button>
                <Button
                  variant={filters.datePreset === 'monthly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('monthly')}
                >
                  Monthly
                </Button>
                <Button
                  variant={filters.datePreset === 'yearly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('yearly')}
                >
                  Yearly
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <PeriodNavigator
                onPrev={() => shiftDateRangeByPreset('prev')}
                onNext={() => shiftDateRangeByPreset('next')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateFrom"
                id="rx-due-soon-date-from"
                value={filters.dateFrom}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateFrom: date || prev.dateFrom, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateTo"
                id="rx-due-soon-date-to"
                value={filters.dateTo}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateTo: date || prev.dateTo, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Rx Type</InputLabel>
                <Select
                  value={filters.rxType || 'glasses'}
                  label="Rx Type"
                  onChange={(e) => setFilters(prev => ({ ...prev, rxType: e.target.value, page: 1 }))}
                >
                  <MenuItem value="glasses">Glasses</MenuItem>
                  <MenuItem value="contacts">Contacts</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <SelectLocation
                location={filters.locationId}
                setLocation={(location) => setFilters(prev => ({
                  ...prev,
                  locationId: location ? location.id : null,
                  page: 1
                }))}
                fullWidth
                required
                helperText="Required"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size='small'>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={filters.limit}
                  label="Items per page"
                  onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {selectedReport === 'rx-expired' && (
          <>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={filters.datePreset === 'daily' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('daily')}
                >
                  Daily
                </Button>
                <Button
                  variant={filters.datePreset === 'monthly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('monthly')}
                >
                  Monthly
                </Button>
                <Button
                  variant={filters.datePreset === 'yearly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyDatePreset('yearly')}
                >
                  Yearly
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <PeriodNavigator
                onPrev={() => shiftDateRangeByPreset('prev')}
                onNext={() => shiftDateRangeByPreset('next')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateFrom"
                id="rx-expired-date-from"
                value={filters.dateFrom}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateFrom: date || prev.dateFrom, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePickerComponent
                name="dateTo"
                id="rx-expired-date-to"
                value={filters.dateTo}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, dateTo: date || prev.dateTo, page: 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Rx Type</InputLabel>
                <Select
                  value={filters.rxType || 'glasses'}
                  label="Rx Type"
                  onChange={(e) => setFilters(prev => ({ ...prev, rxType: e.target.value, page: 1 }))}
                >
                  <MenuItem value="glasses">Glasses</MenuItem>
                  <MenuItem value="contacts">Contacts</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <SelectLocation
                location={filters.locationId}
                setLocation={(location) => setFilters(prev => ({
                  ...prev,
                  locationId: location ? location.id : null,
                  page: 1
                }))}
                fullWidth
                required
                helperText="Required"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size='small'>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={filters.limit}
                  label="Items per page"
                  onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {selectedReport === 'individual-customer' && (
          <>
            <Grid item xs={12}>
              <CustomerSearch
                onCustomerSelected={(customer) => setFilters(prev => ({ 
                  ...prev, 
                  customerId: customer ? customer.id : null 
                }))}
                disableAdd={true}
                sx={{ width: '50%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePickerComponent
                name="startDate"
                id="individual-customer-start-date"
                value={filters.startDate}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, startDate: date || prev.startDate }))}
                helperText="Optional - Filter from this date"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePickerComponent
                name="endDate"
                id="individual-customer-end-date"
                value={filters.endDate}
                onDateSelectionDone={(date) => setFilters(prev => ({ ...prev, endDate: date || prev.endDate }))}
                helperText="Optional - Filter to this date"
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default ReportFilters;
